import React, { Suspense, lazy } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionTitle from './SectionTitle';

const Globe3D = lazy(() => import('./Globe3D'));

const AboutSection = styled.section`
  padding: 120px 56px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 64px;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 48px;
  }
`;

const DecoContainer = styled(motion.div)`
  flex: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;

`;

const TextContainer = styled(motion.div)`
  flex: 1;
`;

const TextLine = styled(motion.p)`
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const lineVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

const About = () => (
  <AboutSection id="about">
    <SectionTitle>About Us</SectionTitle>
    <ContentWrapper>
      <DecoContainer
        initial={{ opacity: 0, x: -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        viewport={{ once: true }}
      >
        <Suspense fallback={null}>
          <Globe3D />
        </Suspense>
      </DecoContainer>

      <TextContainer
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <TextLine variants={lineVariants}>
          At <Highlight>C&amp;D Software</Highlight>, we are passionate about delivering innovative
          software solutions that drive business success. Our team of experienced developers and
          designers work closely with clients to understand their unique needs and craft custom
          solutions that exceed expectations.
        </TextLine>
        <TextLine variants={lineVariants}>
          From <Highlight>web development</Highlight> to <Highlight>mobile applications</Highlight>{' '}
          and <Highlight>cloud services</Highlight>, we are dedicated to helping businesses thrive in
          the digital age. Our expertise spans modern technologies and frameworks, ensuring robust and
          scalable solutions for your business.
        </TextLine>
        <TextLine variants={lineVariants}>
          We believe in building long-term partnerships with our clients, providing ongoing support
          and maintenance to ensure your software continues to evolve with your business needs.
        </TextLine>
      </TextContainer>
    </ContentWrapper>
  </AboutSection>
);

export default About;
